import React from 'react';
import { Course } from '../../api/courses';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { BookOpen, Clock, Users, GraduationCap } from "lucide-react";

interface ViewCourseProps {
    course: Course | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const ViewCourse: React.FC<ViewCourseProps> = ({ course, open, onOpenChange }) => {
    if (!course) return null;

    const getStatusVariant = (status: string) => {
        switch (status) {
            case 'actif': return 'default';
            case 'inactif': return 'secondary';
            case 'archive': return 'outline';
            default: return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'actif': return 'Actif';
            case 'inactif': return 'Inactif';
            case 'archive': return 'Archivé';
            default: return status;
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <div className="flex justify-between items-start pr-8">
                        <div>
                            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                                {course.name}
                                <Badge variant="outline" className="ml-2">
                                    {course.code}
                                </Badge>
                            </DialogTitle>
                            <DialogDescription className="mt-1">
                                Créé le {new Date(course.createdAt).toLocaleDateString('fr-FR')}
                            </DialogDescription>
                        </div>
                        <Badge variant={getStatusVariant(course.status)} className="capitalize">
                            {getStatusLabel(course.status)}
                        </Badge>
                    </div>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                    <div className="space-y-6">
                        <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-2">Détails académiques</h4>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                        <GraduationCap className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">Année & Semestre</p>
                                        <p className="text-sm text-muted-foreground">
                                            {course.year}ème année • {course.semester.map(s => s === '1' ? '1er' : '2ème').join(', ')} sem
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
                                        <BookOpen className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">Crédits</p>
                                        <p className="text-sm text-muted-foreground">{course.credits} crédits</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <h4 className="text-sm font-medium text-muted-foreground mb-2">Organisation</h4>
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                                        <Clock className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">Volume horaire</p>
                                        <p className="text-sm text-muted-foreground">{course.hours} heures</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center text-green-600">
                                        <Users className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">Étudiants actifs</p>
                                        <p className="text-sm text-muted-foreground">{course.currentStudents} étudiants</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {course.description && (
                        <div className="col-span-1 md:col-span-2 mt-4 p-4 bg-muted/30 rounded-lg border">
                            <h4 className="text-sm font-medium mb-2">Description du cours</h4>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {course.description}
                            </p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ViewCourse;
